import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, ShieldCheck } from "lucide-react";
import SectionReveal from "./SectionReveal";

const MODELS = [
  {
    name: "GTS Super",
    code: "gts-super",
    tag: "Sport · Touring",
    type: "neu",
    img: "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/nbewz2oq_imgi_1_GTS-Super.png",
    note: "Italienischer Charakter, sportliche Dynamik.",
    tone: "from-[#fff6f4] to-[#ffe5e1]",
  },
  {
    name: "GTS",
    code: "gts",
    tag: "Klassisch · Komfort",
    type: "neu",
    img: "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/zk52y40w_imgi_2_GTS.png",
    note: "Die elegante Begleiterin für jeden Anlass.",
    tone: "from-[#f8f6f1] to-[#ece5d8]",
  },
  {
    name: "Primavera",
    code: "primavera",
    tag: "Lifestyle · Stadt",
    type: "neu",
    img: "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/z437t4tb_imgi_3_Primavera.png",
    note: "Die ewige Frühlings-Ikone — frisch, leicht, urban.",
    tone: "from-[#eef1f5] to-[#dde3eb]",
  },
  {
    name: "Sprint",
    code: "sprint",
    tag: "Sportlich · Jung",
    type: "neu",
    img: "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/tqsnblcf_imgi_4_Sprint.png",
    note: "Schärfere Linien, mehr Charakter.",
    tone: "from-[#fff3f3] to-[#ffd9d9]",
  },
  {
    name: "946 · 10° Anniversario",
    code: "946-10-anniversario",
    tag: "Limited · Collector",
    type: "neu",
    img: "https://customer-assets.emergentagent.com/job_vespa-basel-relaunch/artifacts/17psyaiz_imgi_5_946-10-Anniversario.png",
    note: "Skulptur auf zwei Rädern. Limitiertes Sammlerstück.",
    tone: "from-[#f4f7ee] to-[#e2ebd0]",
  },
];

const CATEGORIES = [
  { key: "alle", label: "Alle Fahrzeuge" },
  { key: "neu", label: "Neufahrzeuge" },
  { key: "occasion", label: "Occasionen" },
];

export default function Showroom() {
  const [cat, setCat] = useState("alle");

  const visible = useMemo(() => {
    if (cat === "alle") return MODELS;
    return MODELS.filter((m) => m.type === cat);
  }, [cat]);

  return (
    <section
      id="vespa"
      className="section relative bg-[var(--cream)] overflow-hidden"
      data-testid="showroom-section"
    >
      <div className="container-wide">
        {/* Trust strip */}
        <SectionReveal>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12 pb-10 border-b border-[var(--border)]">
            <div className="flex items-center gap-3">
              <ShieldCheck size={18} strokeWidth={1.3} className="text-[var(--sage-dark)]" />
              <p className="font-serif text-xl md:text-2xl text-[var(--anthracite)] italic">
                Hier findest Du Vespa-Liebhaber Angebote{" "}
                <span className="not-italic text-[var(--sage-dark)] font-medium">
                  mit Garantie.
                </span>
              </p>
            </div>
            <a
              href="tel:+41614218833"
              className="overline text-[var(--anthracite)] hover:text-[var(--sage-dark)] transition-colors whitespace-nowrap"
              data-testid="showroom-call"
            >
              +41 61 421 88 33
            </a>
          </div>
        </SectionReveal>

        {/* Filter */}
        <SectionReveal className="mb-10">
          <div
            className="flex flex-wrap gap-2 md:gap-3 items-center"
            data-testid="showroom-filter"
          >
            <span className="overline text-[10px] mr-3 hidden md:inline">Kategorie</span>
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                data-testid={`filter-${c.key}`}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-all duration-300 ${
                  cat === c.key
                    ? "bg-[var(--anthracite)] text-[var(--cream)] border-[var(--anthracite)]"
                    : "bg-transparent text-[var(--anthracite)] border-[var(--border)] hover:border-[var(--anthracite)]"
                }`}
              >
                {c.label}
              </button>
            ))}
            <span className="ml-auto text-[11px] uppercase tracking-[0.2em] text-[var(--muted-fg)]">
              {visible.length}{" "}
              {visible.length === 1 ? "Modell" : "Modelle"}
            </span>
          </div>
        </SectionReveal>

        {/* Uniform shop grid */}
        {visible.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {visible.map((m, i) => (
              <SectionReveal key={m.code} delay={i}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.4 }}
                  className="card-clean relative h-full flex flex-col"
                  data-testid={`model-card-${m.code}`}
                >
                  <Link to="/casa-markus" className="block group h-full flex flex-col">
                    {/* Image area */}
                    <div
                      className={`relative aspect-[4/3] overflow-hidden bg-gradient-to-br ${m.tone}`}
                    >
                      <div className="absolute inset-0 grain" />
                      <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                        <BadgeCheck size={12} className="text-[var(--sage-dark)]" />
                        <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--sage-dark)] font-medium">
                          {m.type === "neu" ? "Neufahrzeug" : "Occasion"}
                        </span>
                      </div>
                      <img
                        src={m.img}
                        alt={m.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    {/* Info */}
                    <div className="p-6 md:p-7 flex-1 flex flex-col justify-between border-t border-[var(--border)]">
                      <div>
                        <span className="overline text-[9px]">{m.tag}</span>
                        <h3 className="font-serif text-2xl md:text-3xl text-[var(--anthracite)] mt-2 leading-tight">
                          {m.name}
                        </h3>
                        <p className="mt-3 text-sm text-[var(--muted-fg)] leading-relaxed">
                          {m.note}
                        </p>
                      </div>
                      <div className="mt-6 flex items-center justify-between pt-5 border-t border-dashed border-[var(--border)]">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--anthracite)] group-hover:text-[var(--sage-dark)] transition-colors">
                          Anfragen
                        </span>
                        <span className="w-9 h-9 grid place-items-center bg-[var(--anthracite)] text-[var(--cream)] transition-transform duration-500 group-hover:rotate-45">
                          <ArrowUpRight size={14} strokeWidth={1.5} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              </SectionReveal>
            ))}
          </div>
        ) : (
          <SectionReveal>
            <div
              className="text-center py-20 border border-dashed border-[var(--border)]"
              data-testid="no-models"
            >
              <p className="font-serif text-3xl text-[var(--anthracite)] italic">
                Aktuell keine Modelle in dieser Kategorie.
              </p>
              <p className="mt-4 text-sm text-[var(--muted-fg)]">
                Rufen Sie uns an — wir finden für jeden die passende Vespa.
              </p>
              <a
                href="tel:+41614218833"
                className="inline-flex mt-6 btn-ghost"
                data-testid="no-models-call"
              >
                +41 61 421 88 33
              </a>
            </div>
          </SectionReveal>
        )}

        <SectionReveal className="mt-16 text-center">
          <Link to="/casa-markus" className="btn-primary" data-testid="showroom-cta-consult">
            Beratungstermin vereinbaren
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </SectionReveal>
      </div>
    </section>
  );
}
